import { jsonRequestHeaders } from "@/src/shared/api/jsonRequestHeaders";
import type { WebMcpToolCallResult, WebMcpToolDefinition } from "../types/webMcp";

type WebMcpErrorResponse = { error?: { message?: string } };

const isJsonResponse = (response: Response): boolean =>
  (response.headers.get("Content-Type") ?? "").includes("application/json");

const isAbortError = (error: unknown): boolean => error instanceof DOMException && error.name === "AbortError";

const toolErrorResult = (message: string): WebMcpToolCallResult => ({
  content: [{ type: "text", text: message }],
  isError: true,
});

const httpErrorMessage = (status: number): string => `Tool call failed with HTTP status ${status}`;

const parseErrorMessage = (text: string): string | undefined => {
  try {
    return (JSON.parse(text) as WebMcpErrorResponse).error?.message;
  } catch {
    return undefined;
  }
};

// Error responses may carry an empty body (e.g. `head :forbidden`), so the body is only
// parsed once it is known to be non-empty JSON.
const errorMessageFrom = async (response: Response): Promise<string> => {
  const fallback = httpErrorMessage(response.status);
  if (!isJsonResponse(response)) return fallback;

  const text = await response.text();
  return (text && parseErrorMessage(text)) || fallback;
};

export class WebMcpApi {
  public async fetchToolDefinitions(): Promise<WebMcpToolDefinition[]> {
    const response = await fetch(`${window.urlPrefix}/web_mcp/tools`, {
      headers: jsonRequestHeaders(),
      credentials: "same-origin",
    });
    if (!response.ok || !isJsonResponse(response)) return [];

    const payload = (await response.json()) as { tools?: WebMcpToolDefinition[] };
    return Array.isArray(payload.tools) ? payload.tools : [];
  }

  public async callTool(
    name: string,
    input: Record<string, unknown>,
    signal?: AbortSignal
  ): Promise<WebMcpToolCallResult> {
    try {
      return await this.performToolCall(name, input, signal);
    } catch (error) {
      if (isAbortError(error)) throw error;

      return toolErrorResult(error instanceof Error ? error.message : "Tool call failed");
    }
  }

  private async performToolCall(
    name: string,
    input: Record<string, unknown>,
    signal?: AbortSignal
  ): Promise<WebMcpToolCallResult> {
    const response = await fetch(`${window.urlPrefix}/web_mcp/tool_calls`, {
      method: "POST",
      headers: jsonRequestHeaders(),
      credentials: "same-origin",
      body: JSON.stringify({ name, arguments: input }),
      signal,
    });
    if (!response.ok) return toolErrorResult(await errorMessageFrom(response));
    if (!isJsonResponse(response)) return toolErrorResult(httpErrorMessage(response.status));

    return (await response.json()) as WebMcpToolCallResult;
  }
}

export const webMcpApi = new WebMcpApi();
